import React, { useState, useCallback, useMemo, forwardRef } from "react";
import { SlArrowDown, SlCalender } from "react-icons/sl";
import { MdOutlineCancel } from "react-icons/md";
import dayjs from "dayjs";
import { Controller, useFormContext } from "react-hook-form";
import { Root, Trigger, Content, Portal } from "@radix-ui/react-popover";
import { ChevronLeft, ChevronRight } from "lucide-react";
import classNames from "classnames";
import { Button } from "components/common/buttons/Button";
import { safeFormatDate } from "utils/dateFormatters";

interface DatePickerMultiProps {
  disabled?: boolean;
  name: string;
  value?: string[];
  handleChange?: (dates: string[]) => void;
  label?: string;
  minDate?: Date;
  placeholder?: string;
  disabledDates?: string[];
  background?: string;
  ariaLabel?: string;
}

export const DatePickerMulti = forwardRef<HTMLDivElement, DatePickerMultiProps>(
  (
    {
      disabled = false,
      name,
      value: _value,
      handleChange: _handleChange,
      label = "Select Dates",
      minDate,
      placeholder = "Add available date range",
      disabledDates = [],
      background = "",
      ariaLabel: _ariaLabel = "Date selection",
    },
    _ref
  ) => {
    const { control } = useFormContext();
    const [isOpen, setIsOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(dayjs());
    const [rangeStart, setRangeStart] = useState<dayjs.Dayjs | null>(null);
    const [rangeEnd, setRangeEnd] = useState<dayjs.Dayjs | null>(null);
    const [hoveredDate, setHoveredDate] = useState<dayjs.Dayjs | null>(null);

    // Memoize styles to prevent unnecessary re-renders
    const baseStyle = useMemo(
      () =>
        "w-full bg-muted border border-border text-foreground transition duration-200 ease-linear appearance-none focus:outline-none text-regular-2 px-3 !py-2",
      []
    );

    const withRoundBorder = useMemo(
      () => "rounded-md hover:border-primary/50 focus:border-primary/50",
      []
    );

    // Format date for display using safe formatting
    const formatDate = useCallback((dateStr: string) => {
      return safeFormatDate(dateStr, "MMM D, YYYY");
    }, []);

    // Generate calendar days
    const generateCalendarDays = useCallback(() => {
      const startOfMonth = currentMonth.startOf("month");
      const endOfMonth = currentMonth.endOf("month");
      const startOfWeek = startOfMonth.startOf("week");
      const endOfWeek = endOfMonth.endOf("week");

      const days = [];
      let day = startOfWeek;

      while (day.isBefore(endOfWeek) || day.isSame(endOfWeek, "day")) {
        days.push(day);
        day = day.add(1, "day");
      }

      return days;
    }, [currentMonth]);

    // Generate date range between two dates
    const generateDateRange = useCallback((start: dayjs.Dayjs, end: dayjs.Dayjs): string[] => {
      const dates: string[] = [];
      let current = start;

      while (current.isBefore(end) || current.isSame(end, "day")) {
        dates.push(current.format("YYYY-MM-DD"));
        current = current.add(1, "day");
      }

      return dates;
    }, []);

    const calendarDays = generateCalendarDays();

    return (
      <div className="flex flex-col gap-1 w-full">
        {label && (
          <label className="block text-muted-foreground text-sm" id={`${name}-label`}>
            {label}
          </label>
        )}

        <Controller
          control={control}
          name={name}
          render={({ field }) => {
            const selectedDates: string[] = field.value || [];

            const handleDateClick = useCallback(
              (date: dayjs.Dayjs) => {
                const dateStr = date.format("YYYY-MM-DD");

                // Check if date is disabled
                if (minDate && date.isBefore(dayjs(minDate), "day")) return;
                if (disabledDates.includes(dateStr)) return;

                if (!rangeStart || (rangeStart && rangeEnd)) {
                  // Start new range
                  setRangeStart(date);
                  setRangeEnd(null);
                } else if (rangeStart && !rangeEnd) {
                  // Complete the range
                  const start = date.isBefore(rangeStart) ? date : rangeStart;
                  const end = date.isBefore(rangeStart) ? rangeStart : date;

                  setRangeEnd(end);

                  // Generate all dates in range
                  const newRangeDates = generateDateRange(start, end);
                  const allSelected = newRangeDates.every((d) => selectedDates.includes(d));
                  let allDates;
                  if (allSelected) {
                    // Unselect all dates in the range
                    allDates = selectedDates.filter((d) => !newRangeDates.includes(d));
                  } else {
                    // Add all dates in the range
                    const existingDates = selectedDates.filter((d) => !newRangeDates.includes(d));
                    allDates = [...existingDates, ...newRangeDates];
                  }
                  field.onChange(allDates);

                  // Reset range selection
                  setRangeStart(null);
                  setRangeEnd(null);
                  setIsOpen(false);
                }
              },
              [
                field,
                selectedDates,
                minDate,
                disabledDates,
                rangeStart,
                rangeEnd,
                generateDateRange,
              ]
            );

            const removeDate = useCallback(
              (dateToRemove: string) => {
                const newDates = selectedDates.filter((date) => date !== dateToRemove);
                field.onChange(newDates);
              },
              [field, selectedDates]
            );

            const isDateSelected = useCallback(
              (date: dayjs.Dayjs) => {
                return selectedDates.includes(date.format("YYYY-MM-DD"));
              },
              [selectedDates]
            );

            const isDateInRange = useCallback(
              (date: dayjs.Dayjs) => {
                if (!rangeStart || !rangeEnd) {
                  if (rangeStart && !rangeEnd) {
                    return date.isSame(rangeStart, "day");
                  }
                  return false;
                }
                return (
                  date.isSameOrAfter(rangeStart, "day") && date.isSameOrBefore(rangeEnd, "day")
                );
              },
              [rangeStart, rangeEnd]
            );

            const isDateInPreviewRange = useCallback(
              (date: dayjs.Dayjs) => {
                if (!rangeStart || rangeEnd || !hoveredDate) return false;
                // Show preview range from rangeStart to hoveredDate
                const start = rangeStart;
                const end = hoveredDate;
                const actualStart = end.isBefore(start) ? end : start;
                const actualEnd = end.isBefore(start) ? start : end;
                return (
                  date.isSameOrAfter(actualStart, "day") && date.isSameOrBefore(actualEnd, "day")
                );
              },
              [rangeStart, rangeEnd, hoveredDate]
            );

            const isDateDisabled = useCallback(
              (date: dayjs.Dayjs) => {
                const dateStr = date.format("YYYY-MM-DD");
                return (
                  (minDate && date.isBefore(dayjs(minDate), "day")) ||
                  disabledDates.includes(dateStr)
                );
              },
              [minDate, disabledDates]
            );

            return (
              <>
                <Root open={isOpen} onOpenChange={setIsOpen}>
                  <Trigger asChild disabled={disabled}>
                    <button
                      type="button"
                      className={classNames(
                        baseStyle,
                        withRoundBorder,
                        background,
                        "flex items-center justify-between cursor-pointer",
                        disabled && "opacity-50 cursor-not-allowed"
                      )}
                      aria-labelledby={`${name}-label`}
                      aria-expanded={isOpen}
                    >
                      <span
                        className={classNames(
                          "text-left flex-1",
                          selectedDates.length === 0 && "text-muted-foreground"
                        )}
                      >
                        {selectedDates.length > 0
                          ? `${selectedDates.length} date${
                              selectedDates.length > 1 ? "s" : ""
                            } selected`
                          : placeholder}
                      </span>
                      <div className="flex items-center gap-2">
                        <SlCalender className="text-primary/50" aria-hidden="true" />
                        <SlArrowDown className="text-primary/50" aria-hidden="true" />
                      </div>
                    </button>
                  </Trigger>

                  <Portal>
                    <Content
                      className="z-[9999] w-80 bg-card border border-border rounded-lg p-4 shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
                      sideOffset={5}
                      align="start"
                      onInteractOutside={() => setIsOpen(false)}
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {/* Calendar Header */}
                      <div className="flex items-center justify-between mb-4">
                        <Button
                          type="button"
                          color="gray"
                          size="icon"
                          className="h-7 w-7"
                          aria-label="Previous month"
                          onClick={() => setCurrentMonth(currentMonth.subtract(1, "month"))}
                        >
                          <ChevronLeft aria-hidden />
                        </Button>
                        <h3 className="text-foreground font-medium">
                          {currentMonth.format("MMMM YYYY")}
                        </h3>
                        <Button
                          type="button"
                          color="gray"
                          size="icon"
                          className="h-7 w-7"
                          aria-label="Next month"
                          onClick={() => setCurrentMonth(currentMonth.add(1, "month"))}
                        >
                          <ChevronRight aria-hidden />
                        </Button>
                      </div>

                      {/* Instructions */}
                      <div className="mb-3 text-center text-xs text-muted-foreground">
                        {!rangeStart
                          ? "Click a date to start selecting a range"
                          : !rangeEnd
                            ? "Click another date to complete the range"
                            : "Range selected"}
                      </div>

                      {/* Calendar Grid */}
                      <div
                        className="grid grid-cols-7 gap-1 mb-4"
                        onMouseLeave={() => setHoveredDate(null)}
                      >
                        {/* Day headers */}
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                          <div
                            key={day}
                            className="py-2 text-center text-xs font-medium text-muted-foreground"
                          >
                            {day}
                          </div>
                        ))}

                        {/* Calendar days */}
                        {calendarDays.map((day, index) => {
                          const isCurrentMonth = day.month() === currentMonth.month();
                          const isSelected = isDateSelected(day);
                          const isInRange = isDateInRange(day);
                          const isInPreviewRange = isDateInPreviewRange(day);
                          const isDisabled = isDateDisabled(day);
                          const isToday = day.isSame(dayjs(), "day");
                          const isRangeStart = rangeStart && day.isSame(rangeStart, "day");
                          const isRangeEnd = rangeEnd && day.isSame(rangeEnd, "day");

                          return (
                            <button
                              key={index}
                              type="button"
                              aria-pressed={Boolean(isSelected || isInRange)}
                              onClick={() => handleDateClick(day)}
                              onMouseEnter={() => setHoveredDate(day)}
                              disabled={isDisabled}
                              className={classNames(
                                "h-8 w-8 text-sm rounded flex items-center justify-center transition-colors relative",
                                {
                                  "bg-muted/50 text-muted-foreground/50": !isCurrentMonth,
                                  "text-foreground":
                                    isCurrentMonth &&
                                    !isSelected &&
                                    !isInRange &&
                                    !isInPreviewRange,
                                  "bg-primary text-primary-foreground font-medium": isSelected,
                                  "bg-primary/10 text-foreground": isInRange && !isSelected,
                                  "bg-primary/20 text-foreground":
                                    isInPreviewRange && !isSelected && !isInRange,
                                  "bg-primary/20 text-primary":
                                    isToday && !isSelected && !isInRange && !isInPreviewRange,
                                  "opacity-50 cursor-not-allowed": isDisabled,
                                  "hover:bg-muted":
                                    !isSelected &&
                                    !isDisabled &&
                                    isCurrentMonth &&
                                    !isInRange &&
                                    !isInPreviewRange,
                                  "ring-2 ring-primary": isRangeStart || isRangeEnd,
                                }
                              )}
                            >
                              {day.date()}
                            </button>
                          );
                        })}
                      </div>
                    </Content>
                  </Portal>
                </Root>

                {/* Selected dates display */}
                {selectedDates.length > 0 && (
                  <div className="mt-4">
                    <div id={`${name}-description`} className="text-muted-foreground mb-2">
                      Selected Dates:
                    </div>
                    <div className="flex flex-wrap gap-2" role="list" aria-label="Selected dates">
                      {selectedDates
                        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
                        .map((date: string, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-1 rounded-md bg-muted px-3 py-1 text-foreground"
                            role="listitem"
                          >
                            <span>{formatDate(date)}</span>
                            {!disabled && (
                              <Button
                                type="button"
                                color="gray"
                                size="icon"
                                className="h-5 w-5 rounded-sm [&_svg]:h-3.5 [&_svg]:w-3.5"
                                onClick={() => removeDate(date)}
                                aria-label={`Remove date ${formatDate(date)}`}
                              >
                                <MdOutlineCancel aria-hidden="true" />
                              </Button>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            );
          }}
        />
      </div>
    );
  }
);

DatePickerMulti.displayName = "DatePickerMulti";
